import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CreditCard, Bell, Settings as Gear, Save, RotateCcw } from 'lucide-react';
import api from '../services/api';
import { useI18n } from '../i18n/I18nProvider';
import { useSession } from '../session/SessionProvider';
import { errorText } from '../lib/errors';
import { Card, CardHeader, CardBody, Field, Input, Switch, Button, LoadingBlock } from '../ui';

const DEFAULT_THEME = {
  primary_color: '#007AFF',
  secondary_color: '#34C759',
  accent_color: '#5856D6',
  background_color: '#0F0F12',
  card_gradient: ['#007AFF', '#00C6FF'],
  corner_radius: 16,
  logo_url: '',
  header_title: '',
};
const TAB_IDS = ['wallets', 'notifications', 'settings'];
const TAB_ICONS = { wallets: CreditCard, notifications: Bell, settings: Gear };
const TAB_SF = { wallets: 'creditcard', notifications: 'bell', settings: 'gear' };
const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v);

/** Оформление мобильного приложения компании с превью телефона */
export default function BrandingEditor({ tenantId, companyName }) {
  const { t } = useI18n();
  const { canEdit } = useSession();
  const [theme, setTheme] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/tenants/${tenantId}/config`);
      const data = res.data.data || {};
      const loadedTheme = { ...DEFAULT_THEME, ...(data.theme || {}) };
      loadedTheme.logo_url = loadedTheme.logo_url || '';
      loadedTheme.header_title = loadedTheme.header_title || companyName || '';
      const serverTabs = data.navigation?.tabs || [];
      const loadedTabs = TAB_IDS.map((id, i) => {
        const found = serverTabs.find((tab) => tab.id === id);
        return { id, icon: TAB_SF[id], title: found?.title || t(`branding.tabNames.${id}`), is_enabled: found ? found.is_enabled : true, order: found?.order || i + 1 };
      });
      setTheme(loadedTheme);
      setTabs(loadedTabs);
      setInitial({ theme: loadedTheme, tabs: loadedTabs });
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  }, [tenantId, companyName, t]);

  useEffect(() => { load(); }, [load]);

  if (!theme) return <LoadingBlock />;

  const setColor = (key, value) => setTheme((th) => ({ ...th, [key]: value }));
  const setGradient = (i, value) => setTheme((th) => {
    const next = [...th.card_gradient];
    next[i] = value;
    return { ...th, card_gradient: next };
  });

  const colorsValid = ['primary_color', 'secondary_color', 'accent_color', 'background_color'].every((k) => isHex(theme[k]))
    && theme.card_gradient.every(isHex);
  const dirty = JSON.stringify({ theme, tabs }) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/tenants/${tenantId}/config`, {
        theme: { ...theme, logo_url: theme.logo_url.trim() || null, corner_radius: Number(theme.corner_radius) },
        navigation: { tabs },
      });
      setInitial({ theme, tabs });
      toast.success(t('branding.saved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const colorField = (key, label) => (
    <Field label={label} key={key}>
      <ColorInput value={theme[key]} onChange={(v) => setColor(key, v)} disabled={!canEdit} />
    </Field>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <Card>
          <CardHeader title={t('branding.title')} description={t('branding.subtitle')} />
          <CardBody className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('branding.headerTitle')}>
                <Input value={theme.header_title} onChange={(e) => setTheme({ ...theme, header_title: e.target.value })} maxLength={40} disabled={!canEdit} />
              </Field>
              <Field label={t('branding.logoUrl')}>
                <Input type="url" value={theme.logo_url} onChange={(e) => setTheme({ ...theme, logo_url: e.target.value })} placeholder="https://…" disabled={!canEdit} />
              </Field>
            </div>
            <div>
              <p className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('branding.colors')}</p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {colorField('primary_color', t('branding.primary'))}
                {colorField('secondary_color', t('branding.secondary'))}
                {colorField('accent_color', t('branding.accent'))}
                {colorField('background_color', t('branding.background'))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('branding.cardGradient')}>
                <div className="grid grid-cols-2 gap-2">
                  <ColorInput value={theme.card_gradient[0]} onChange={(v) => setGradient(0, v)} disabled={!canEdit} />
                  <ColorInput value={theme.card_gradient[1]} onChange={(v) => setGradient(1, v)} disabled={!canEdit} />
                </div>
              </Field>
              <Field label={`${t('branding.cornerRadius')}: ${theme.corner_radius}px`}>
                <input
                  type="range" min="0" max="32" step="1"
                  value={theme.corner_radius}
                  onChange={(e) => setTheme({ ...theme, corner_radius: Number(e.target.value) })}
                  disabled={!canEdit}
                  className="mt-2 w-full accent-brand-600"
                />
              </Field>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('branding.tabsTitle')} description={t('branding.tabsHint')} />
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {tabs.map((tab, i) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <li key={tab.id} className="flex items-center gap-3 px-5 py-3">
                  <Icon className="h-4 w-4 shrink-0 text-zinc-400" />
                  <Input
                    value={tab.title}
                    onChange={(e) => setTabs(tabs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                    maxLength={24}
                    disabled={!canEdit}
                    aria-label={t(`branding.tabNames.${tab.id}`)}
                  />
                  <Switch
                    checked={tab.is_enabled}
                    onChange={(v) => setTabs(tabs.map((x, j) => (j === i ? { ...x, is_enabled: v } : x)))}
                    disabled={!canEdit || (tab.id === 'wallets')}
                    label={tab.title}
                  />
                </li>
              );
            })}
          </ul>
        </Card>

        {canEdit && (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" icon={RotateCcw} disabled={!dirty || saving} onClick={() => { setTheme(initial.theme); setTabs(initial.tabs); }}>
              {t('common.cancel')}
            </Button>
            <Button icon={Save} onClick={save} loading={saving} disabled={!dirty || !colorsValid}>{t('common.save')}</Button>
          </div>
        )}
      </div>

      <div className="lg:sticky lg:top-4 lg:self-start">
        <p className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('branding.preview')}</p>
        <PhonePreview theme={theme} tabs={tabs} />
      </div>
    </div>
  );
}

function ColorInput({ value, onChange, disabled }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={isHex(value) ? value : '#000000'}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        disabled={disabled}
        className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-zinc-300 bg-white p-0.5 disabled:cursor-not-allowed dark:border-zinc-700 dark:bg-zinc-900"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        maxLength={7}
        disabled={disabled}
        className={`font-mono ${isHex(value) ? '' : 'border-red-400 dark:border-red-700'}`}
      />
    </div>
  );
}

/** Упрощённый макет экрана карт приложения */
function PhonePreview({ theme, tabs }) {
  const { t } = useI18n();
  const radius = Number(theme.corner_radius) || 0;
  return (
    <div className="mx-auto w-[280px] rounded-[2.5rem] border-[10px] border-zinc-900 bg-zinc-900 shadow-pop dark:border-zinc-700">
      <div className="relative h-[540px] overflow-hidden rounded-[1.9rem]" style={{ background: theme.background_color }}>
        <div className="mx-auto mt-2 h-5 w-24 rounded-full bg-black/80" />
        <div className="flex items-center gap-2 px-5 pt-5">
          {theme.logo_url ? (
            <img src={theme.logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <span className="h-8 w-8 rounded-lg" style={{ background: theme.primary_color }} />
          )}
          <p className="truncate text-base font-bold text-white">{theme.header_title}</p>
        </div>
        <div
          className="mx-5 mt-6 p-5 text-white shadow-lg"
          style={{ borderRadius: radius, background: `linear-gradient(135deg, ${theme.card_gradient[0]}, ${theme.card_gradient[1]})` }}
        >
          <p className="text-xs opacity-80">{t('branding.previewCard')}</p>
          <p className="mt-6 text-xs opacity-80">{t('branding.previewBalance')}</p>
          <p className="text-2xl font-bold">1 250 ₴</p>
        </div>
        <div className="mx-5 mt-4 h-11 text-center text-sm font-semibold leading-[2.75rem] text-white" style={{ borderRadius: radius, background: `linear-gradient(90deg, ${theme.primary_color}, ${theme.secondary_color})` }}>
          QR
        </div>
        <div className="mx-5 mt-4 space-y-2">
          {[0, 1].map((i) => <div key={i} className="h-10 rounded-lg bg-white/5" />)}
        </div>
        <div className="absolute inset-x-0 bottom-0 flex justify-around border-t border-white/10 bg-black/40 px-2 pb-5 pt-2 backdrop-blur">
          {tabs.filter((tab) => tab.is_enabled).map((tab, i) => {
            const Icon = TAB_ICONS[tab.id];
            return (
              <div key={tab.id} className="flex flex-col items-center gap-0.5 text-[10px]" style={{ color: i === 0 ? theme.primary_color : '#9ca3af' }}>
                <Icon className="h-4 w-4" />
                <span className="max-w-[72px] truncate">{tab.title}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
